import React, { useState } from 'react';
import { Container } from '@mui/material';

const FAQ = () => {
	const faqs = [
		{
			id: 'how-it-works',
			question: 'What is the AlphaReview Smart NFC Review Card and how does it work?',
			answer:
				'AlphaReview is a smart PVC business card embedded with contactless NFC technology and a dynamic QR code on the back. When a customer taps their phone or scans the QR code at your checkout counter, dining table, or reception desk, your customized review landing page pops up automatically on their phone without any app install. Delighted customers post directly on Google Maps, while our built-in AI drafts authentic positive reviews to save them time.',
			category: 'Overview',
		},
		{
			id: 'no-app-needed',
			question: 'Does the customer need to install any app or register to leave a review?',
			answer:
				'No, absolutely zero apps or logins required! It runs natively using the smartphone’s built-in NFC reader (Apple Pay / Google Wallet hardware) or the standard phone camera for QR scanning. It instantly opens your review gateway in their default mobile browser.',
			category: 'Customer Experience',
		},
		{
			id: 'negative-reviews',
			question: 'How does the Smart Shield prevent negative reviews from hurting my Google rating?',
			answer:
				'AlphaReview uses smart sentiment routing: when a happy customer selects 4 or 5 stars, they are seamlessly guided to publish publicly on Google Maps, TripAdvisor, or Zomato. If a customer gives 1, 2, or 3 stars, they are automatically directed to a private, constructive grievance form that sends feedback directly to your private dashboard and WhatsApp. This allows you to resolve issues with the customer privately instead of receiving a public 1-star penalty.',
			category: 'Reputation Protection',
		},
		{
			id: 'pricing-fees',
			question: 'Are there any hidden monthly subscription fees or renewal costs?',
			answer:
				'None at all. AlphaReview is a one-time purchase with lifetime validity starting at ₹999 for 3 NFC cards. You get unlimited scans, unlimited taps, AI review generation, and private merchant portal access forever with zero monthly or annual charges.',
			category: 'Pricing',
		},
		{
			id: 'compatibility',
			question: 'Which smartphones work with AlphaReview NFC cards?',
			answer:
				'All modern smartphones are fully supported. 100% of iPhones (iPhone 7 and newer) read NFC natively without touching any settings. Over 95% of Android smartphones from Samsung, OnePlus, Xiaomi, Oppo, Vivo, Realme, and Google Pixel have NFC enabled. For any older phone without NFC, the dynamic high-contrast QR code on the card works instantly with any camera app.',
			category: 'Compatibility',
		},
		{
			id: 'multi-platform',
			question: 'Can I route reviews to TripAdvisor, Zomato, or Trustpilot instead of Google?',
			answer:
				'Yes! While Google Business Profile is the most popular, you can customize your review destination to TripAdvisor, Zomato, Trustpilot, Facebook Reviews, Practo, or your own website directly from your private admin portal anytime.',
			category: 'Integrations',
		},
		{
			id: 'shipping-delivery',
			question: 'How fast is delivery and shipping across India?',
			answer:
				'We ship pan-India via express air couriers (Bluedart, Delhivery, DTDC). Cards are encoded, quality checked, and dispatched within 24 to 48 hours, arriving at your doorstep in 3 to 5 business days anywhere in India with full live tracking.',
			category: 'Shipping',
		},
		{
			id: 'google-compliance',
			question: 'Is AlphaReview compliant with Google’s review guidelines and policies?',
			answer:
				'Yes, 100% compliant. AlphaReview facilitates authentic customer reviews from genuine in-person visitors. Customers publish reviews using their own personal Google accounts directly on Google Maps, adhering fully to Google’s guidelines.',
			category: 'Compliance',
		},
	];

	const [openIndex, setOpenIndex] = useState(0);

	const toggleFaq = (index) => {
		setOpenIndex(openIndex === index ? null : index);
	};

	return (
		<section id='faq' className='py-20 bg-slate-50 border-t border-slate-200'>
			<Container maxWidth='lg'>
				<div className='text-center max-w-3xl mx-auto mb-14'>
					<span className='inline-flex items-center px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200 mb-3'>
						Frequently Asked Questions
					</span>
					<h2 className='text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight'>
						Got Questions? We’ve Got Answers.
					</h2>
					<p className='text-slate-600 text-base sm:text-lg mt-3'>
						Everything you need to know about AlphaReview NFC cards, Google rating scaling, and reputation protection.
					</p>
				</div>

				<div className='max-w-3xl mx-auto space-y-3.5'>
					{faqs.map((faq, index) => {
						const isOpen = openIndex === index;
						return (
							<div
								key={faq.id}
								className={`rounded-2xl border transition-all duration-200 bg-white ${
									isOpen
										? 'border-blue-500 shadow-sm'
										: 'border-slate-200 hover:border-slate-300'
								}`}>
								<button
									onClick={() => toggleFaq(index)}
									className='w-full px-6 py-4 sm:py-5 flex items-center justify-between text-left focus:outline-none'
									aria-expanded={isOpen}>
									<span className='text-base sm:text-lg font-bold text-slate-900 pr-4'>
										{faq.question}
									</span>
									<span
										className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-transform duration-200 ${
											isOpen
												? 'rotate-45 bg-blue-50 text-blue-600'
												: 'bg-slate-100 text-slate-500'
										}`}>
										+
									</span>
								</button>
								{isOpen && (
									<div className='px-6 pb-6 pt-1 text-slate-600 text-sm sm:text-base leading-relaxed border-t border-slate-100'>
										<p>{faq.answer}</p>
										<div className='mt-3 flex items-center gap-2 text-xs font-semibold text-slate-400'>
											<span className='px-2 py-0.5 rounded bg-slate-100 text-slate-600'>{faq.category}</span>
											<span>•</span>
											<span className='text-emerald-600 font-bold'>✓ Verified</span>
										</div>
									</div>
								)}
							</div>
						);
					})}
				</div>

				{/* Still have questions */}
				<div className='mt-12 text-center bg-white rounded-2xl p-8 border border-slate-200 max-w-2xl mx-auto shadow-sm'>
					<h3 className='text-xl font-bold text-slate-900'>Have a question about your business setup?</h3>
					<p className='text-slate-600 text-sm mt-1'>
						Our support team in Bangalore is ready to help you get started with the right card setup.
					</p>
					<div className='mt-5 flex flex-wrap items-center justify-center gap-3'>
						<a
							href='https://wa.me/919880889303'
							target='_blank'
							rel='noopener noreferrer'
							className='inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm transition-all'>
							<i className='fa fa-whatsapp text-base'></i>
							<span>WhatsApp Support (+91 98808 89303)</span>
						</a>
						<a
							href='mailto:hello@alphareview.in'
							className='inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all'>
							<span>Email Support</span>
						</a>
					</div>
				</div>
			</Container>
		</section>
	);
};

export default FAQ;
